import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  RefreshCw, Package, Calendar, Phone, MapPin, Truck, Store, 
  Play, Pause, X, Send, Search, Filter, Clock, CheckCircle2,
  AlertCircle, Plus, Pencil, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSubscriptions = ({ adminToken }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 10;

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [todaySubscriptions, setTodaySubscriptions] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);

  // Form state for creating/editing subscriptions
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    product_id: '',
    quantity: 1,
    frequency: 'daily',
    delivery_type: 'delivery',
    delivery_address: '',
    start_date: getTomorrowDate(),
    end_date: '',
    delivery_days: []
  });

  const weekDays = [
    { id: 'monday', label: 'Mon' },
    { id: 'tuesday', label: 'Tue' },
    { id: 'wednesday', label: 'Wed' },
    { id: 'thursday', label: 'Thu' },
    { id: 'friday', label: 'Fri' },
    { id: 'saturday', label: 'Sat' }
  ];

  useEffect(() => {
    loadFresh();
    fetchTodaySubscriptions();
    fetchProducts();
  }, [adminToken, statusFilter]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?include_out_of_stock=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchSubscriptions = loadFresh; // alias for backward compat in dialogs

  async function loadFresh(skpAmt = 0) {
    if (skpAmt === 0) setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.append('status', statusFilter);
    params.append('limit', LIMIT);
    params.append('skip', skpAmt);
    try {
      const response = await axios.get(`${API}/admin/subscriptions?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = response.data;
      if (skpAmt === 0) {
        setSubscriptions(data.subscriptions);
      } else {
        setSubscriptions(prev => [...prev, ...data.subscriptions]);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
      setSkip(skpAmt);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }

  const loadMore = async () => {
    setLoadingMore(true);
    await loadFresh(skip + LIMIT);
    setLoadingMore(false);
  };

  const fetchTodaySubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscriptions/today`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTodaySubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Failed to fetch today subscriptions:', error);
    }
  };

  const handleGenerateOrder = async () => {
    if (!selectedSub) return;
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscriptions/${selectedSub.id}/generate-order`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`Order generated! Total: ₹${response.data.total} (Delivery: ₹${response.data.delivery_charge || 0})`);
      
      if (response.data.payment_link) {
        window.open(response.data.payment_link, '_blank');
      }
      
      setShowGenerateDialog(false);
      loadFresh(0);
      fetchTodaySubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate order');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAllToday = async () => {
    setGeneratingAll(true);
    try {
      await axios.post(
        `${API}/admin/subscriptions/generate-all-today`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Generating all today\'s subscription orders in background. Refreshing...');
      setTimeout(() => { loadFresh(0); fetchTodaySubscriptions(); }, 3000);
    } catch (error) {
      toast.error('Failed to trigger bulk generation');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!formData.phone || !formData.name || !formData.product_id || !formData.end_date) {
      toast.error('Please fill all required fields');
      return;
    }
    if (formData.delivery_type === 'delivery' && !formData.delivery_address) {
      toast.error('Please enter delivery address');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscriptions/create`,
        {
          phone: formData.phone,
          name: formData.name,
          product_id: formData.product_id,
          quantity: formData.quantity,
          frequency: formData.frequency,
          delivery_type: formData.delivery_type,
          delivery_address: formData.delivery_address,
          start_date: formData.start_date,
          end_date: formData.end_date,
          delivery_days: formData.frequency === 'weekly' ? formData.delivery_days : null
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`Subscription created! ${response.data.total_deliveries} deliveries scheduled.`);
      setShowCreateDialog(false);
      resetForm();
      loadFresh(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubscription = async () => {
    if (!selectedSub) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/subscriptions/${selectedSub.id}`,
        {
          quantity: formData.quantity,
          delivery_type: formData.delivery_type,
          delivery_address: formData.delivery_address,
          end_date: formData.end_date,
          status: formData.status
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success('Subscription updated!');
      setShowEditDialog(false);
      loadFresh(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      delivery_days: prev.delivery_days.includes(day)
        ? prev.delivery_days.filter(d => d !== day)
        : [...prev.delivery_days, day]
    }));
  };

  const resetForm = () => {
    setFormData({
      phone: '',
      name: '',
      product_id: '',
      quantity: 1,
      frequency: 'daily',
      delivery_type: 'delivery',
      delivery_address: '',
      start_date: getTomorrowDate(),
      end_date: '',
      delivery_days: []
    });
  };

  const openEditDialog = (sub) => {
    setSelectedSub(sub);
    setFormData({
      phone: sub.user_phone || '',
      name: sub.user_name || sub.name || '',
      product_id: sub.product_id,
      quantity: sub.quantity,
      frequency: sub.frequency,
      delivery_type: sub.delivery_type,
      delivery_address: sub.delivery_address || '',
      start_date: sub.start_date,
      end_date: sub.end_date,
      delivery_days: sub.delivery_days || [],
      status: sub.status
    });
    setShowEditDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getFrequencyLabel = (freq) => {
    const labels = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly' };
    return labels[freq] || freq;
  };

  const getNextDelivery = (sub) => {
    const today = new Date().toISOString().split('T')[0];
    const upcoming = sub.scheduled_dates?.filter(
      d => d >= today && !sub.completed_dates?.includes(d) && !sub.skipped_dates?.includes(d)
    );
    return upcoming?.[0];
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.user_phone?.toLowerCase().includes(query) ||
      sub.user_name?.toLowerCase().includes(query) ||
      sub.product?.name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Today's Subscriptions Alert */}
      {todaySubscriptions.length > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">
                {todaySubscriptions.length} Subscription{todaySubscriptions.length > 1 ? 's' : ''} Due Today
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Generate orders and send payment links to customers (delivery charges will be calculated automatically)
              </p>
              <div className="mt-3 space-y-2">
                {/* Bulk generate button */}
                {todaySubscriptions.length > 1 && (
                  <Button
                    size="sm"
                    className="w-full bg-amber-700 hover:bg-amber-800 gap-2 mb-2"
                    onClick={handleGenerateAllToday}
                    disabled={generatingAll}
                    data-testid="generate-all-today"
                  >
                    {generatingAll ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Generate All {todaySubscriptions.length} Orders at Once
                  </Button>
                )}
                {todaySubscriptions.map((sub) => (
                  <div 
                    key={sub.id}
                    className="flex items-center justify-between p-2 bg-white rounded-lg border border-amber-200"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-amber-600" />
                      <span className="font-medium text-sm">{sub.product?.name}</span>
                      <span className="text-xs text-muted-foreground">x{sub.quantity}</span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{sub.user_phone}</span>
                      {sub.delivery_type === 'delivery' && (
                        <Badge variant="outline" className="text-xs">
                          <Truck className="h-3 w-3 mr-1" />
                          +Delivery
                        </Badge>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 gap-1"
                      onClick={() => { setSelectedSub(sub); setShowGenerateDialog(true); }}
                    >
                      <Send className="h-3 w-3" />
                      Generate
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters and Create Button */}
      <Card className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search loaded subscriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); }}>
              <SelectTrigger className="w-[130px] h-9 text-sm">
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="h-9 w-9 p-0" onClick={() => loadFresh(0)}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button className="bg-primary gap-1.5 h-9 text-sm" onClick={() => { resetForm(); setShowCreateDialog(true); }}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New</span> Sub
            </Button>
          </div>
        </div>
        {total > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            Showing {subscriptions.length} of {total} subscriptions
            {searchQuery && ` · ${filteredSubscriptions.length} match search`}
          </p>
        )}
      </Card>

      {/* Subscriptions List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Loading subscriptions...</p>
        </div>
      ) : filteredSubscriptions.length === 0 ? (
        <Card className="p-8 text-center">
          <RefreshCw className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold text-accent mb-2">No Subscriptions Found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'Create a subscription for a customer'}
          </p>
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="gap-2">
            <Plus className="h-4 w-4" />
            New Subscription
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSubscriptions.map((sub) => (
            <Card key={sub.id} className="p-3 sm:p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <h3 className="font-semibold text-accent text-sm truncate">{sub.product?.name}</h3>
                      <Badge className={`${getStatusColor(sub.status)} text-xs`}>{sub.status}</Badge>
                      <Badge variant="outline" className="text-xs">{getFrequencyLabel(sub.frequency)}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{sub.user_name || sub.user_phone}</span>
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{sub.user_phone}</span>
                      <span>Qty: {sub.quantity} · ₹{(sub.product?.price || 0) * sub.quantity}/delivery</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <Calendar className="h-3 w-3" />
                      <span>{sub.start_date} → {sub.end_date}</span>
                      {sub.delivery_type === 'pickup' ? <Store className="h-3 w-3" /> : <Truck className="h-3 w-3" />}
                      <span>{sub.delivery_type === 'pickup' ? 'Pickup' : 'Delivery'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  {sub.status === 'active' && getNextDelivery(sub) && (
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Next</p>
                      <p className="text-xs font-semibold text-primary">{getNextDelivery(sub)}</p>
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" onClick={() => openEditDialog(sub)}>
                      <Pencil className="h-3 w-3" />Edit
                    </Button>
                    {sub.status === 'active' && (
                      <Button size="sm" className="h-7 px-2 bg-primary text-xs gap-1" onClick={() => { setSelectedSub(sub); setShowGenerateDialog(true); }}>
                        <Send className="h-3 w-3" />Gen
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Compact delivery progress */}
              <div className="mt-2 pt-2 border-t">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Progress</span>
                  <span className="font-medium">{sub.completed_dates?.length || 0}/{sub.scheduled_dates?.length || 0} delivered</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {sub.scheduled_dates?.slice(0, 10).map((date) => {
                    const isCompleted = sub.completed_dates?.includes(date);
                    const isSkipped = sub.skipped_dates?.includes(date);
                    const today = new Date().toISOString().split('T')[0];
                    const isToday = date === today;
                    return (
                      <span key={date} className={`text-[10px] px-1 py-0.5 rounded ${
                        isCompleted ? 'bg-green-100 text-green-700' :
                        isSkipped ? 'bg-gray-100 text-gray-400 line-through' :
                        isToday ? 'bg-amber-100 text-amber-700 font-bold ring-1 ring-amber-300' :
                        'bg-gray-50 text-gray-600'
                      }`}>
                        {date.slice(5)}
                      </span>
                    );
                  })}
                  {sub.scheduled_dates?.length > 10 && (
                    <span className="text-[10px] px-1 py-0.5 text-muted-foreground">+{sub.scheduled_dates.length - 10}</span>
                  )}
                </div>
              </div>
            </Card>
          ))}

          {/* Show More */}
          {hasMore && !searchQuery && (
            <div className="pt-1">
              <Button
                variant="outline"
                className="w-full h-10 gap-2 text-sm"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="show-more-subscriptions"
              >
                {loadingMore
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /> Loading...</>
                  : <>Show More ({total - subscriptions.length} remaining)</>
                }
              </Button>
            </div>
          )}
          {!hasMore && subscriptions.length > 0 && total > LIMIT && !searchQuery && (
            <p className="text-center text-xs text-muted-foreground py-2">All {total} subscriptions loaded</p>
          )}
        </div>
      )}

      {/* Generate Order Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Subscription Order</DialogTitle>
            <DialogDescription>
              This will create an order with delivery charges and generate a Razorpay payment link.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSub && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Package className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-semibold">{selectedSub.product?.name}</p>
                    <p className="text-sm text-muted-foreground">Quantity: {selectedSub.quantity}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedSub.user_name || selectedSub.user_phone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Delivery Type</p>
                  <p className="font-medium capitalize">{selectedSub.delivery_type}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Product Amount</p>
                  <p className="text-xl font-bold text-primary">
                    ₹{(selectedSub.product?.price || 0) * selectedSub.quantity}
                    {selectedSub.delivery_type === 'delivery' && (
                      <span className="text-sm font-normal text-muted-foreground ml-1">+ delivery (auto-calculated)</span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {selectedSub.delivery_type === 'delivery' 
                    ? 'Delivery charges will be calculated based on the customer\'s address. A payment link will be sent via SMS.'
                    : 'This is a pickup order. A payment link will be sent via SMS.'}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateOrder} disabled={generating} className="bg-primary gap-2">
              {generating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Generate Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Subscription Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Create Subscription for Client
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Customer Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Customer name"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Product Selection */}
            <div>
              <Label>Product *</Label>
              <Select value={formData.product_id} onValueChange={(v) => setFormData({ ...formData, product_id: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} - ₹{p.price}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantity and Frequency */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity per Delivery</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily (Mon-Sat)</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Weekly Day Selection */}
            {formData.frequency === 'weekly' && (
              <div>
                <Label>Delivery Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {weekDays.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => handleDayToggle(day.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        formData.delivery_days.includes(day.id)
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  min={getTomorrowDate()}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Starts from tomorrow</p>
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  min={formData.start_date}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Delivery Type */}
            <div>
              <Label>Delivery Type</Label>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, delivery_type: 'delivery' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    formData.delivery_type === 'delivery' ? 'border-primary bg-primary/5' : 'border-gray-200'
                  }`}
                >
                  <Truck className="h-4 w-4" />
                  Delivery
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, delivery_type: 'pickup' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    formData.delivery_type === 'pickup' ? 'border-primary bg-primary/5' : 'border-gray-200'
                  }`}
                >
                  <Store className="h-4 w-4" />
                  Pickup
                </button>
              </div>
            </div>

            {/* Delivery Address */}
            {formData.delivery_type === 'delivery' && (
              <div>
                <Label>Delivery Address *</Label>
                <Textarea
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                  placeholder="Complete delivery address for auto-calculating delivery charges"
                  rows={2}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Delivery charges will be calculated based on this address</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSubscription} disabled={saving} className="gap-2">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
              Create Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Edit Subscription
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="font-semibold">{selectedSub?.product?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedSub?.user_name || selectedSub?.user_phone}</p>
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quantity */}
            <div>
              <Label>Quantity per Delivery</Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                min="1"
                className="mt-1"
              />
            </div>

            {/* End Date */}
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
                className="mt-1"
              />
            </div>

            {/* Delivery Type */}
            <div>
              <Label>Delivery Type</Label>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, delivery_type: 'delivery' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    formData.delivery_type === 'delivery' ? 'border-primary bg-primary/5' : 'border-gray-200'
                  }`}
                >
                  <Truck className="h-4 w-4" />
                  Delivery
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, delivery_type: 'pickup' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    formData.delivery_type === 'pickup' ? 'border-primary bg-primary/5' : 'border-gray-200'
                  }`}
                >
                  <Store className="h-4 w-4" />
                  Pickup
                </button>
              </div>
            </div>

            {/* Delivery Address */}
            {formData.delivery_type === 'delivery' && (
              <div>
                <Label>Delivery Address</Label>
                <Textarea
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                  placeholder="Complete delivery address"
                  rows={2}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSubscription} disabled={saving} className="gap-2">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptions;
